import React from "react";
import { 
  UserCheck, UserPlus, Building2, GraduationCap, Globe, BookOpen, 
  TrendingUp, Briefcase, Zap, CheckCircle, ArrowRight, ShieldCheck, 
  Search, MapPin, Database, Award, Info, FileText, BarChart3, AlertTriangle, Layers, Lock
} from "lucide-react";
import { AUDIENCE_MODES, AudienceModeId, getAudienceModeConfig } from "../config/audienceModes";
import { getSeoEntryByRoute } from "../utils/seoRegistry";
import { useSeoMetadata } from "./PublicPages";
import { SeoBreadcrumbs } from "./seo/SeoBreadcrumbs";
import { 
  SeoSearchActionModule, 
  SeoWhyPageExistsSection, 
  SeoExampleResultsPreview, 
  SeoTrustSignalBlock, 
  SeoCtaFooterBanner,
  SampleBusinessCard 
} from "./seo/SeoUniversalPageEngine";
import { PricingComparisonTable } from "./PricingComparisonTable";

import { GUIDE_REGISTRY } from "../utils/guideRegistry";

interface AudienceLandingPageProps {
  route: string;
  onNavigate: (path: string) => void;
  onSelectAudienceMode?: (modeId: AudienceModeId) => void;
  onLogin?: (isSignUp?: boolean) => void;
}

const ROUTE_TO_MODE_MAP: Record<string, AudienceModeId> = {
  "/for-job-seekers": "job_seeker",
  "/for-recruiters": "recruiter",
  "/for-staffing-agencies": "staffing_agency",
  "/for-career-coaches": "career_coach",
  "/for-workforce-organizations": "workforce_organization",
  "/for-college-career-centers": "college_career_center",
  "/for-sales-teams": "sales_professional",
  "/for-business-development": "business_development",
  "/for-agencies": "agency_freelancer",
  "/recruiter-business-search": "recruiter",
  "/employer-prospecting": "recruiter",
  "/staffing-prospecting": "staffing_agency",
  "/local-business-prospecting": "sales_professional",
  "/business-hiring-signals": "job_seeker",
  "/hidden-jobs-near-me": "job_seeker",
  "/local-businesses-hiring": "job_seeker",
  "/companies-hiring-without-job-postings": "job_seeker",
  "/small-businesses-hiring-near-me": "job_seeker",
  "/local-job-search-tool": "job_seeker",
  "/business-opportunity-finder": "job_seeker",
  "/local-lead-finder": "sales_professional",
  "/staffing-agency-leads": "staffing_agency",
  "/career-coach-job-search-tool": "career_coach",
  "/workforce-development-business-search": "workforce_organization",
  "/college-career-center-employer-search": "college_career_center",
  "/sales-prospecting-local-businesses": "sales_professional",
  "/business-development-local-opportunities": "business_development",
  "/agency-client-finder": "agency_freelancer",
  "/business-explorer": "business_development"
};

const ICON_MAP: Record<string, React.ElementType> = {
  UserCheck,
  UserPlus,
  Building2,
  GraduationCap,
  Globe,
  BookOpen,
  TrendingUp,
  Briefcase,
  Zap
};

export const AudienceLandingPage: React.FC<AudienceLandingPageProps> = ({
  route,
  onNavigate,
  onSelectAudienceMode,
  onLogin
}) => {
  // Check if it is an audience-specific route or a methodology route
  const modeId = ROUTE_TO_MODE_MAP[route];
  
  if (modeId) {
    return (
      <AudienceModeLandingView
        modeId={modeId}
        route={route}
        onNavigate={onNavigate}
        onSelectAudienceMode={onSelectAudienceMode}
        onLogin={onLogin}
      />
    );
  }

  // Handle Methodology & Data Pages
  if (route === "/hiring-signal-methodology") {
    return <MethodologyHiringSignalView route={route} onNavigate={onNavigate} />;
  }

  if (route === "/opportunity-score-methodology") {
    return <MethodologyScoreView route={route} onNavigate={onNavigate} />;
  }

  if (route === "/data-sources") {
    return <DataSourcesView route={route} onNavigate={onNavigate} />;
  }

  if (route === "/accuracy-and-limitations") {
    return <AccuracyLimitationsView route={route} onNavigate={onNavigate} />;
  }

  // Fallback to job seeker view
  return (
    <AudienceModeLandingView
      modeId="job_seeker"
      route={route}
      onNavigate={onNavigate}
      onSelectAudienceMode={onSelectAudienceMode}
      onLogin={onLogin}
    />
  );
};

// 1. Audience Mode Landing View Component
const AudienceModeLandingView: React.FC<{
  modeId: AudienceModeId;
  route: string;
  onNavigate: (path: string) => void;
  onSelectAudienceMode?: (modeId: AudienceModeId) => void;
  onLogin?: (isSignUp?: boolean) => void;
}> = ({ modeId, route, onNavigate, onSelectAudienceMode, onLogin }) => {
  const seoEntry = getSeoEntryByRoute(route);
  const config = getAudienceModeConfig(modeId);
  const Icon = ICON_MAP[config.iconName] || UserCheck;

  const canonicalUrl = seoEntry?.canonicalUrl || `https://getbiznearyou.com${route}`;
  const title = seoEntry?.title || `BizNearYou for ${config.label} | BizNearYou`;
  const description = seoEntry?.metaDescription || `${config.tagline}. ${config.description}`;
  const heroH1 = seoEntry?.h1 || config.heroHeadline;

  useSeoMetadata({
    title,
    description,
    canonicalUrl,
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: `BizNearYou - ${config.label} Edition`,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web Browser",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD"
      },
      description: description
    }
  });

  const handleLaunchMode = () => {
    if (onSelectAudienceMode) {
      onSelectAudienceMode(modeId);
    }
    onNavigate("/dashboard");
  };

  const audienceSampleCards: SampleBusinessCard[] = [
    {
      name: "Aegis Health & Commercial Tech",
      category: "Tech & Healthcare",
      location: "Austin, TX Metro",
      signalTier: "high",
      signalBadgeText: "High Signal",
      reason: `Active office intake portal updated recently with target ${config.shortLabel} opportunity indicators.`,
      freshness: "Verified 1 day ago",
      verifiability: "Verified Public Web Data"
    },
    {
      name: "Summit Medical & Dental Group",
      category: "Healthcare Practice",
      location: "Nashville, TN",
      signalTier: "possible",
      signalBadgeText: "Possible Signal",
      reason: "25+ new patient reviews this month indicating clinical practice expansion.",
      freshness: "Verified 3 days ago",
      verifiability: "Public Business Registry"
    },
    {
      name: "Vanguard Logistics & Supply",
      category: "Logistics & Operations",
      location: "Chicago, IL",
      signalTier: "unconfirmed",
      signalBadgeText: "Unconfirmed Signal",
      reason: "Expanded fleet registration and new department contacts indexed.",
      freshness: "Verified 5 days ago",
      verifiability: "Review Stream Index"
    }
  ];

  return (
    <div className="bg-slate-50 dark:bg-slate-950 min-h-screen text-slate-800 dark:text-slate-100 pb-20">
      {/* Top Breadcrumb Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 py-3 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <SeoBreadcrumbs items={[
            { label: "Home", path: "/" },
            { label: "Solutions", path: "/solutions" },
            { label: config.label, path: route }
          ]} onNavigate={onNavigate} />

          <button
            type="button"
            onClick={handleLaunchMode}
            className="hidden sm:inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-extrabold bg-blue-600 text-white hover:bg-blue-700 transition cursor-pointer"
          >
            <span>Launch {config.shortLabel} App</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Hero Banner Section */}
      <section className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white pt-10 sm:pt-14 pb-14 sm:pb-20 px-4 sm:px-6 lg:px-8 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-5xl mx-auto text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 dark:bg-blue-950/80 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-900/60 text-xs font-bold uppercase tracking-wider">
            <Icon className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 shrink-0" />
            <span>{modeId === "job_seeker" ? "BUILT FOR LOCAL JOB SEARCH" : `BUILT FOR ${config.label.toUpperCase()}S`}</span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 dark:text-white leading-tight max-w-4xl mx-auto">
            {heroH1}
          </h1>

          <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed font-medium">
            {config.heroSubheadline}
          </p>

          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <button
              type="button"
              onClick={handleLaunchMode}
              className="w-full sm:w-auto px-6 py-3.5 sm:px-8 sm:py-4 rounded-xl font-extrabold text-sm sm:text-base bg-blue-600 hover:bg-blue-700 text-white transition shadow-xs hover:shadow-md flex items-center justify-center gap-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
            >
              <span>{modeId === "job_seeker" ? "Explore Local Employers" : `Explore as ${config.shortLabel}`}</span>
              <ArrowRight className="w-4 h-4 shrink-0" />
            </button>

            {onLogin && (
              <button
                type="button"
                onClick={() => onLogin(true)}
                className="w-full sm:w-auto px-6 py-3.5 sm:px-8 sm:py-4 rounded-xl font-bold text-sm sm:text-base bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/80 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 transition flex items-center justify-center gap-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
              >
                <span>Create Free Account</span>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Interactive Search Module */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <SeoSearchActionModule
          defaultLocation=""
          defaultKeyword=""
          onNavigate={onNavigate}
        />
      </div>

      {/* Example Results Preview Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <SeoExampleResultsPreview
          cards={audienceSampleCards}
          locationName={`${config.label} Directory`}
          onNavigate={onNavigate}
          onLogin={onLogin}
        />
      </div>

      {/* Why This Page Exists Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <SeoWhyPageExistsSection
          locationName={`${config.label} Sourcing`}
          contextDescription={`Discover how BizNearYou empowers ${config.label}s to bypass traditional channels and engage verified business owners directly.`}
        />
      </div>

      {/* Primary Value Pillars & Terminology Overview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
        <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-xl space-y-8">
          <div>
            <h2 className="text-xl font-black text-slate-900 tracking-tight mb-2">
              How BizNearYou Adapts for {config.label} Workflows
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 max-w-3xl leading-relaxed">
              BizNearYou re-indexes local commercial business data dynamically based on your chosen audience perspective. Here is how terminology and ranking logic change:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-1">
              <span className="text-[10px] font-extrabold uppercase text-slate-400 tracking-wider">Opportunity Term</span>
              <div className="font-extrabold text-sm text-slate-900">{config.terminology.opportunityTerm}</div>
              <p className="text-[11px] text-slate-500">Every matched business record is framed around this target concept.</p>
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-1">
              <span className="text-[10px] font-extrabold uppercase text-slate-400 tracking-wider">Signal Focus</span>
              <div className="font-extrabold text-sm text-slate-900">{config.terminology.signalTerm}</div>
              <p className="text-[11px] text-slate-500">Filters emphasize public evidence aligned with your goals.</p>
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-1">
              <span className="text-[10px] font-extrabold uppercase text-slate-400 tracking-wider">Scoring Model</span>
              <div className="font-extrabold text-sm text-indigo-700">{config.terminology.scoreTerm}</div>
              <p className="text-[11px] text-slate-500">Scores re-weight hiring recency, web gaps, and location density.</p>
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-1">
              <span className="text-[10px] font-extrabold uppercase text-slate-400 tracking-wider">Pipeline Focus</span>
              <div className="font-extrabold text-sm text-slate-900">{config.terminology.pipelineTitle}</div>
              <p className="text-[11px] text-slate-500">CRM pipeline stages match your outreach lifecycle.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Preset Searches Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-black text-slate-900 tracking-tight">
                Recommended Search Presets for {config.label}s
              </h3>
              <p className="text-xs text-slate-500">
                Click any preset to open the search workspace configured for {config.shortLabel} mode.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {config.searchPresets.map((preset, idx) => (
              <div
                key={idx}
                onClick={handleLaunchMode}
                className="bg-white p-5 rounded-xl border border-slate-200 hover:border-indigo-400 shadow-xs hover:shadow-md transition cursor-pointer space-y-2 group"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-indigo-600 group-hover:text-indigo-800">Preset #{idx + 1}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-indigo-600 transition-transform group-hover:translate-x-1" />
                </div>
                <h4 className="font-extrabold text-sm text-slate-900 group-hover:text-indigo-600 transition-colors">
                  {preset.label}
                </h4>
                <p className="text-xs text-slate-500 leading-snug">
                  {preset.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Pipeline Stages */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 space-y-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-700">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-black text-slate-900">
                {config.terminology.pipelineTitle} Structure
              </h3>
              <p className="text-xs text-slate-500">
                Kanban CRM stages pre-configured for {config.label} workflows.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            {config.pipelineStages.map((stage) => (
              <div
                key={stage.id}
                className={`px-3.5 py-2 rounded-xl text-xs font-extrabold border border-slate-200 flex items-center gap-2 ${stage.color}`}
              >
                <span className="w-2 h-2 rounded-full bg-current"></span>
                <span>{stage.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Comparison Table Context */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 space-y-6">
        <div className="text-center max-w-2xl mx-auto space-y-2">
          <h2 className="text-2xl font-black text-slate-900 tracking-tight">
            Compare Free vs Pro Plans for {config.label}s
          </h2>
          <p className="text-xs text-slate-500">
            See which features are available on the Free tier and unlock advanced tools with Pro.
          </p>
        </div>

        <PricingComparisonTable onNavigate={onNavigate} />
      </section>

      {/* Featured Strategic Guides Section for this Audience */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 space-y-6">
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Featured Guides & Tactical Playbooks for {config.label}s
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              In-depth strategies, outreach scripts, and market analysis tailored for {config.shortLabel} workflows.
            </p>
          </div>
          <a
            href="/guides"
            onClick={(e) => {
              e.preventDefault();
              onNavigate("/guides");
            }}
            className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 cursor-pointer"
          >
            <span>All Guides</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(() => {
            const MODE_GUIDES: Record<AudienceModeId, string[]> = {
              recruiter: ["recruitment-lead-generation", "how-to-find-recruiting-clients", "hiring-intent-signals"],
              staffing_agency: ["staffing-agency-lead-generation", "how-to-get-clients-for-a-staffing-agency", "how-to-find-businesses-that-need-staffing"],
              career_coach: ["job-search-tools-for-career-coaches", "how-to-help-clients-find-hidden-jobs", "how-to-build-an-employer-target-list"],
              workforce_organization: ["employer-sourcing-for-workforce-programs", "how-workforce-programs-find-local-employers", "how-to-build-an-employer-target-list"],
              college_career_center: ["how-career-centers-find-employers", "how-to-find-internship-employers", "how-to-find-an-internship-near-you"],
              sales_professional: ["how-to-find-growing-businesses", "how-to-find-newly-opened-businesses", "business-growth-signals"],
              business_development: ["how-to-find-growing-businesses", "business-growth-signals", "how-to-build-a-local-business-prospect-list"],
              agency_freelancer: ["how-agencies-find-local-clients", "business-growth-signals", "how-to-find-newly-opened-businesses"],
              business_explorer: ["how-to-find-growing-businesses", "business-growth-signals", "how-to-build-a-local-business-prospect-list"],
              job_seeker: ["how-to-ask-if-a-business-is-hiring", "how-to-find-hidden-jobs", "how-to-email-a-business-about-a-job"]
            };
            const slugs = MODE_GUIDES[modeId] || ["how-to-ask-if-a-business-is-hiring", "how-to-find-hidden-jobs", "how-to-email-a-business-about-a-job"];

            return slugs.map((slug) => {
              const guide = GUIDE_REGISTRY[slug];
              if (!guide) return null;
              return (
                <div
                  key={slug}
                  className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-xs hover:shadow-md transition flex flex-col justify-between space-y-4"
                >
                  <div className="space-y-3">
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                      {guide.category}
                    </span>
                    <h3 className="font-bold text-base text-slate-900 dark:text-white leading-snug">
                      <a
                        href={`/guides/${guide.slug}`}
                        onClick={(e) => {
                          e.preventDefault();
                          onNavigate(`/guides/${guide.slug}`);
                        }}
                        className="hover:text-indigo-600 dark:hover:text-indigo-400 transition cursor-pointer"
                      >
                        {guide.h1}
                      </a>
                    </h3>
                    <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-3 leading-relaxed">
                      {guide.metaDescription}
                    </p>
                  </div>
                  <a
                    href={`/guides/${guide.slug}`}
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigate(`/guides/${guide.slug}`);
                    }}
                    className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline inline-flex items-center gap-1 cursor-pointer pt-2 border-t border-slate-100 dark:border-slate-800"
                  >
                    <span>Read Full Playbook</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              );
            });
          })()}
        </div>
      </section>
    </div>
  );
};

// 2. Methodology: Hiring Signal View
const MethodologyHiringSignalView: React.FC<{
  route: string;
  onNavigate: (path: string) => void;
}> = ({ route, onNavigate }) => {
  useSeoMetadata({
    title: "Hiring Signal Methodology & Science | BizNearYou",
    description: "Detailed breakdown of direct vs inferred hiring signals, confidence algorithms, recency decay, and false-positive prevention.",
    canonicalUrl: "https://getbiznearyou.com/hiring-signal-methodology"
  });

  return (
    <div className="bg-slate-50 min-h-screen text-slate-800 pb-20">
      <div className="bg-white border-b border-slate-200 py-3 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SeoBreadcrumbs items={[
            { label: "Home", path: "/" },
            { label: "Methodology", path: "/methodology" },
            { label: "Hiring Signals", path: route }
          ]} onNavigate={onNavigate} />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 space-y-10">
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">
            <BarChart3 className="w-4 h-4" />
            <span>Detection Methodology</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
            Hiring Signal Detection Science & Confidence Engine
          </h1>
          <p className="text-base text-slate-600 leading-relaxed">
            BizNearYou evaluates public business data to spot active hiring activity and operational expansion indicators. Here is how signals are detected, classified, and scored.
          </p>
        </div>

        <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-xs space-y-6">
          <h2 className="text-xl font-extrabold text-slate-900">Direct vs. Inferred Signals</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2 bg-slate-50 p-4 rounded-xl border border-slate-200">
              <h3 className="font-extrabold text-sm text-emerald-800 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span>Direct Signals</span>
              </h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Direct evidence gathered from public employer career pages, state business filings, explicit "Now Hiring" banners, or direct employer claim notices.
              </p>
            </div>

            <div className="space-y-2 bg-slate-50 p-4 rounded-xl border border-slate-200">
              <h3 className="font-extrabold text-sm text-indigo-800 flex items-center gap-2">
                <Info className="w-4 h-4" />
                <span>Inferred Signals</span>
              </h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Statistical growth indicators like sudden review volume surges, new location licensing, web service additions, or multi-site registrations.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-xs space-y-4">
          <h2 className="text-xl font-extrabold text-slate-900">Confidence Scoring & Recency Decay</h2>
          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
            Every detected signal decays over time. Signals detected within 14 days carry maximum weight, while older signals decay logarithmically over a 90-day window to ensure stale listings do not clutter user searches.
          </p>
        </div>
      </div>
    </div>
  );
};

// 3. Methodology: Score View
const MethodologyScoreView: React.FC<{
  route: string;
  onNavigate: (path: string) => void;
}> = ({ route, onNavigate }) => {
  useSeoMetadata({
    title: "Opportunity Score Methodology | BizNearYou",
    description: "Detailed mathematical breakdown of the 1-100 Opportunity Score algorithm and multi-audience weighting factors.",
    canonicalUrl: "https://getbiznearyou.com/opportunity-score-methodology"
  });

  return (
    <div className="bg-slate-50 min-h-screen text-slate-800 pb-20">
      <div className="bg-white border-b border-slate-200 py-3 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SeoBreadcrumbs items={[
            { label: "Home", path: "/" },
            { label: "Methodology", path: "/methodology" },
            { label: "Opportunity Score", path: route }
          ]} onNavigate={onNavigate} />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 space-y-10">
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-100 text-indigo-800 text-xs font-bold">
            <Award className="w-4 h-4" />
            <span>Algorithm Specification</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
            Opportunity Score Calculation Engine
          </h1>
          <p className="text-base text-slate-600 leading-relaxed">
            The 1-100 Opportunity Score combines signal recency, employer contactability, business stability, and web presence metrics into a single weighted priority score.
          </p>
        </div>

        <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-xs space-y-4">
          <h2 className="text-xl font-extrabold text-slate-900">Score Component Weights</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs font-bold border-b pb-2">
              <span>Hiring Activity Evidence</span>
              <span className="text-indigo-600">35 - 45%</span>
            </div>
            <div className="flex items-center justify-between text-xs font-bold border-b pb-2">
              <span>Contact Channel Availability (Phone, Web, Email)</span>
              <span className="text-indigo-600">20 - 30%</span>
            </div>
            <div className="flex items-center justify-between text-xs font-bold border-b pb-2">
              <span>Geographic Proximity & Commute Score</span>
              <span className="text-indigo-600">15 - 25%</span>
            </div>
            <div className="flex items-center justify-between text-xs font-bold">
              <span>Web Quality & Tech Opportunities</span>
              <span className="text-indigo-600">10 - 25%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 4. Data Sources View
const DataSourcesView: React.FC<{
  route: string;
  onNavigate: (path: string) => void;
}> = ({ route, onNavigate }) => {
  useSeoMetadata({
    title: "Public Commercial Data Sources | BizNearYou",
    description: "Full disclosure of public APIs, state filings, domain WHOIS records, and location data sources used by BizNearYou.",
    canonicalUrl: "https://getbiznearyou.com/data-sources"
  });

  return (
    <div className="bg-slate-50 min-h-screen text-slate-800 pb-20">
      <div className="bg-white border-b border-slate-200 py-3 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SeoBreadcrumbs items={[
            { label: "Home", path: "/" },
            { label: "Data & Transparency", path: "/transparency" },
            { label: "Data Sources", path: route }
          ]} onNavigate={onNavigate} />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 space-y-10">
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-100 text-blue-800 text-xs font-bold">
            <Database className="w-4 h-4" />
            <span>Data Transparency</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
            Public Commercial Data Sources & Verification Standards
          </h1>
          <p className="text-base text-slate-600 leading-relaxed">
            BizNearYou aggregates public commercial information from authoritative sources. We do not scrape non-public personal data.
          </p>
        </div>

        <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-xs space-y-4">
          <h2 className="text-xl font-extrabold text-slate-900">Data Sources List</h2>
          <ul className="space-y-3 text-xs text-slate-600">
            <li className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <span><strong>Google Places API & Commercial Location Data</strong>: Provides verified business names, physical addresses, phone numbers, website URLs, and geographic coordinates.</span>
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <span><strong>Public State Business Filings</strong>: Verification of active commercial registrations, LLC filings, and corporate status.</span>
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <span><strong>Public Domain WHOIS & Web Metadata</strong>: Detection of active websites, SSL security, and web contact channels.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// 5. Accuracy & Limitations View
const AccuracyLimitationsView: React.FC<{
  route: string;
  onNavigate: (path: string) => void;
}> = ({ route, onNavigate }) => {
  useSeoMetadata({
    title: "Data Accuracy & Product Limitations | BizNearYou",
    description: "Understand signal uncertainty, data freshness policies, and why BizNearYou never guarantees unposted job openings.",
    canonicalUrl: "https://getbiznearyou.com/accuracy-and-limitations"
  });

  return (
    <div className="bg-slate-50 min-h-screen text-slate-800 pb-20">
      <div className="bg-white border-b border-slate-200 py-3 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SeoBreadcrumbs items={[
            { label: "Home", path: "/" },
            { label: "Data & Transparency", path: "/transparency" },
            { label: "Accuracy & Limitations", path: route }
          ]} onNavigate={onNavigate} />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 space-y-10">
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold">
            <AlertTriangle className="w-4 h-4" />
            <span>Product Disclosure</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
            Data Accuracy Standards & Product Limitations
          </h1>
          <p className="text-base text-slate-600 leading-relaxed">
            BizNearYou provides business intelligence based on public commercial data. We maintain high standards of transparency regarding product limitations.
          </p>
        </div>

        <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-xs space-y-4">
          <h2 className="text-xl font-extrabold text-slate-900">Key Disclosures</h2>
          <ul className="space-y-3 text-xs text-slate-600">
            <li className="flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <span><strong>Unconfirmed Vacancy Status</strong>: Opportunity Scores and Hiring Signals indicate potential hiring activity based on growth markers. They do not represent formal job listings or confirmed vacancies.</span>
            </li>
            <li className="flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <span><strong>Data Verification Cycles</strong>: Business records are refreshed periodically. Hours, contact phone numbers, or management changes may occur between syncs.</span>
            </li>
            <li className="flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <span><strong>Opt-Out Compliance</strong>: Any commercial business owner can request listing removal at any time via our Compliance Center.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
